import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  initAuth, fetchRemoteData, pushToRemote, mergeAndRecompute,
  linkEmailToAccount, signInWithEmail,
} from '../services/syncService';

const AppContext = createContext(null);

const STORAGE_KEY = 'recovery_app_data';

const defaultData = {
  name: '',
  streak: 0,
  totalDays: 0,
  bestStreak: 0,
  achievements: [],
  lastCheckIn: null,
};

// Load from localStorage
const loadData = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...defaultData, ...JSON.parse(saved) } : defaultData;
  } catch {
    return defaultData;
  }
};

// Save to localStorage
const saveData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Save error:', e);
  }
};

const LOGS_KEY = 'recovery_app_logs';

const loadLogs = () => {
  try {
    const saved = localStorage.getItem(LOGS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

export const AppProvider = ({ children }) => {
  const [userData, setUserData] = useState(loadData);
  const [dailyLogs, setDailyLogs] = useState(loadLogs);

  // Cloud sync state. `syncReady` gates write-through so we never push a
  // pre-merge local snapshot over a newer remote copy while the initial
  // reconciliation is still in flight.
  const [uid, setUid] = useState(null);
  const [syncReady, setSyncReady] = useState(false);
  const [syncStatus, setSyncStatus] = useState('local'); // 'local' | 'syncing' | 'synced' | 'error'

  // Auto-save locally whenever userData/logs change - unchanged from
  // before, this always happens regardless of whether cloud sync is on.
  useEffect(() => { saveData(userData); }, [userData]);
  useEffect(() => { localStorage.setItem(LOGS_KEY, JSON.stringify(dailyLogs)); }, [dailyLogs]);

  // Sign in (anonymously if needed) and reconcile with whatever's already
  // in the cloud for this uid. Runs once on mount; if Firebase isn't
  // configured, initAuth calls back with uid=null immediately and the app
  // just proceeds local-only, same as before this feature existed.
  useEffect(() => {
    let cancelled = false;
    const unsubscribe = initAuth(async (resolvedUid) => {
      if (cancelled) return;
      if (!resolvedUid) { setSyncReady(true); return; }
      setUid(resolvedUid);
      setSyncStatus('syncing');
      try {
        const remote = await fetchRemoteData(resolvedUid);
        if (remote) {
          const merged = mergeAndRecompute({ userData, dailyLogs }, remote);
          setUserData(merged.userData);
          setDailyLogs(merged.dailyLogs);
          pushToRemote(resolvedUid, merged);
        } else {
          pushToRemote(resolvedUid, { userData, dailyLogs });
        }
        setSyncStatus('synced');
      } catch (e) {
        console.error('Initial sync failed, continuing local-only:', e);
        setSyncStatus('error');
      }
      setSyncReady(true);
    });
    return () => { cancelled = true; unsubscribe(); };
    // Intentionally only runs once on mount - see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Write-through: any local change also gets pushed to the cloud copy,
  // once the initial reconciliation above has finished.
  useEffect(() => {
    if (!syncReady || !uid) return;
    setSyncStatus('syncing');
    pushToRemote(uid, { userData, dailyLogs });
    setSyncStatus('synced');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, dailyLogs, syncReady, uid]);

  const updateStreak = (isClean) => {
    setUserData(prev => {
      const newStreak = isClean ? prev.streak + 1 : 0;
      return {
        ...prev,
        streak: newStreak,
        bestStreak: Math.max(newStreak, prev.bestStreak),
        totalDays: prev.totalDays + 1,
        lastCheckIn: new Date().toISOString(),
      };
    });
  };

  const saveDailyLog = (date, log) => {
    setDailyLogs(prev => ({ ...prev, [date]: log }));
  };

  const getTodayLog = () => {
    const today = new Date().toISOString().split('T')[0];
    return dailyLogs[today] || null;
  };

  const resetAll = () => {
    setUserData(defaultData);
    setDailyLogs({});
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LOGS_KEY);
  };

  // Upgrades the current anonymous session to an email+password identity so
  // the same data can be reached by signing into that email elsewhere.
  const enableCrossDeviceSync = async (email, password) => {
    const newUid = await linkEmailToAccount(email, password);
    setUid(newUid);
    return newUid;
  };

  // Used on a second device: sign into the email already linked on device A.
  const signInForSync = (email, password) => signInWithEmail(email, password);

  return (
    <AppContext.Provider value={{
      userData, setUserData,
      dailyLogs, updateStreak,
      saveDailyLog, getTodayLog,
      resetAll,
      syncStatus, enableCrossDeviceSync, signInForSync,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
